// ============================================================
// xFreeScan by XCloud Solutions - New Scan Page with Tabs
// ============================================================
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scansApi } from '../../lib/api';
import { usePlan } from '../../hooks/usePlan';
import { UpgradePrompt, PlanBadge, ModuleLock } from '../../components/UpgradePrompt';
import ScanContextForm, { type ScanContextItem } from '../../components/ScanContextForm';
import {
  Globe, Server, Lock, Mail, Code, Eye, Wifi, Shield,
  AlertTriangle, CheckCircle, Zap, Rocket, Key, Gauge,
  ShieldOff, MousePointer, Users, FileCode, FileWarning,
  Fingerprint, FolderOpen, UserSearch, AlertCircle, Database,
  Radar, Layers, KeyRound, Bug, Network, Cpu, Search,
  Webhook, GitBranch, ShieldAlert, ClipboardCheck
} from 'lucide-react';

const ICON_MAP: Record<string, any> = {
  Shield, Lock, Globe, Cpu, Network, Bug, Search, AlertTriangle, Database,
  Radar, Zap, Eye, Layers, KeyRound, Key, Gauge, ShieldOff, MousePointer,
  Users, Code, AlertCircle, Fingerprint, FolderOpen, UserSearch, FileCode, FileWarning,
  Webhook, GitBranch, ShieldAlert, ClipboardCheck,
};

interface ScanModule {
  id: string;
  name: string;
  desc: string;
  icon: string;
  tier: 'free' | 'deep' | 'active';
  isActive?: boolean;
}

const FREE_MODULES: ScanModule[] = [
  { id: 'quick', name: 'Escaneo Rapido', desc: 'Ejecuta todos los modulos basicos (headers, SSL, DNS, tech, puertos, web, subdominios, admin panels, CVEs)', icon: 'Shield', tier: 'free' },
  { id: 'headers', name: 'Headers de Seguridad', desc: 'Analiza headers HTTP, cookies, y configuracion de seguridad del servidor', icon: 'Shield', tier: 'free' },
  { id: 'ssl', name: 'SSL/TLS', desc: 'Verifica certificados, protocolos y cifrado', icon: 'Lock', tier: 'free' },
  { id: 'dns', name: 'DNS & Email', desc: 'Valida SPF, DKIM, DMARC, DNSSEC y registros MX', icon: 'Globe', tier: 'free' },
  { id: 'tech', name: 'Tecnologias', desc: 'Detecta CMS, frameworks, librerias y sus versiones', icon: 'Cpu', tier: 'free' },
  { id: 'ports', name: 'Puertos', desc: 'Escaneo basico de puertos comunes (24 puertos)', icon: 'Network', tier: 'free' },
  { id: 'web', name: 'Vulnerabilidades Web', desc: 'Archivos expuestos, CSRF en HTML, librerias desactualizadas', icon: 'Bug', tier: 'free' },
  { id: 'subdomain', name: 'Subdominios', desc: 'Enumeracion pasiva via Certificate Transparency y DNS', icon: 'Search', tier: 'free' },
  { id: 'admin_panels', name: 'Paneles de Admin', desc: 'Detecta paneles de administracion expuestos (/manager, /admin, /wp-admin...)', icon: 'AlertTriangle', tier: 'free' },
  { id: 'cve_mapping', name: 'CVEs Conocidos', desc: 'Mapea tecnologias detectadas a vulnerabilidades CVE con CVSS', icon: 'Database', tier: 'free' },
];

const DEEP_MODULES: ScanModule[] = [
  { id: 'deep', name: 'Escaneo Profundo', desc: 'Todo lo basico + Nmap, Nuclei, Nikto, Subfinder, testssl.sh', icon: 'Rocket', tier: 'deep' },
  { id: 'nmap', name: 'Nmap (1000 puertos)', desc: 'Escaneo completo de puertos con deteccion de servicios y versiones', icon: 'Radar', tier: 'deep' },
  { id: 'nuclei', name: 'Nuclei CVE Scanner', desc: 'Deteccion de vulnerabilidades con templates actualizados (critical/high/medium)', icon: 'Zap', tier: 'deep' },
  { id: 'nikto', name: 'Nikto Web Scanner', desc: 'Escaneo de vulnerabilidades del servidor web y configuracion', icon: 'Eye', tier: 'deep' },
  { id: 'subfinder', name: 'Subfinder + httpx', desc: 'Descubrimiento avanzado de subdominios con probing HTTP', icon: 'Layers', tier: 'deep' },
  { id: 'testssl', name: 'testssl.sh', desc: 'Analisis exhaustivo de configuracion SSL/TLS y cifrado', icon: 'KeyRound', tier: 'deep' },
];

const ACTIVE_MODULES: ScanModule[] = [
  { id: 'pentest', name: 'Pentest Completo', desc: 'Ejecuta TODOS los modulos: basicos + profundos + pentesting activo automatizado', icon: 'Shield', tier: 'active', isActive: true },
  { id: 'brute_login', name: 'Credenciales por Defecto', desc: 'Prueba 120 credenciales comunes en paneles de admin detectados', icon: 'Key', tier: 'active', isActive: true },
  { id: 'rate_limit_test', name: 'Test Rate Limiting', desc: 'Verifica si el login tiene proteccion anti-fuerza bruta (50 intentos)', icon: 'Gauge', tier: 'active', isActive: true },
  { id: 'csrf_active', name: 'CSRF Activo', desc: 'Verifica si formularios aceptan requests cross-origin sin token', icon: 'ShieldOff', tier: 'active', isActive: true },
  { id: 'clickjack_poc', name: 'Clickjacking PoC', desc: 'Genera prueba de concepto de clickjacking si falta X-Frame-Options', icon: 'MousePointer', tier: 'active', isActive: true },
  { id: 'tenant_enum', name: 'Enumeracion de Tenants', desc: 'Detecta IDs secuenciales que exponen informacion de otros clientes', icon: 'Users', tier: 'active', isActive: true },
  { id: 'sqli_test', name: 'SQL Injection', desc: '16 payloads SELECT-only en formularios (UNION, time-based, error-based)', icon: 'Code', tier: 'active', isActive: true },
  { id: 'xss_test', name: 'XSS Reflejado', desc: '5 payloads XSS en parametros y formularios detectados', icon: 'AlertCircle', tier: 'active', isActive: true },
  { id: 'session_analysis', name: 'Analisis de Sesion', desc: 'Recolecta 20 tokens de sesion, analiza entropia y flags de seguridad', icon: 'Fingerprint', tier: 'active', isActive: true },
  { id: 'dir_traversal', name: 'Directory Traversal', desc: '16 variantes de path traversal para acceder a archivos de configuracion', icon: 'FolderOpen', tier: 'active', isActive: true },
  { id: 'user_enum', name: 'Enumeracion de Usuarios', desc: 'Timing analysis para detectar si el servidor diferencia usuarios validos', icon: 'UserSearch', tier: 'active', isActive: true },
  { id: 'js_analysis', name: 'Analisis de JavaScript', desc: 'Extrae endpoints, variables, claves y estructura interna de archivos JS', icon: 'FileCode', tier: 'active', isActive: true },
  { id: 'info_disclosure', name: 'Info Disclosure Avanzada', desc: 'Busca paths especificos por tecnologia (snoop.jsp, phpinfo, server-status)', icon: 'FileWarning', tier: 'active', isActive: true },
  { id: 'oob_callback', name: 'OOB Callback (Blind Vulns)', desc: 'Detecta SSRF/XXE/RCE ciegos via callbacks DNS/HTTP (interactsh)', icon: 'Webhook', tier: 'active', isActive: true },
  { id: 'attack_chains', name: 'Cadenas de Ataque', desc: 'Construye paths de explotacion multi-paso enlazando hallazgos', icon: 'GitBranch', tier: 'active', isActive: true },
  { id: 'waf_detection', name: 'Deteccion de WAF', desc: 'Detecta WAF/IPS y adapta payloads para bypass', icon: 'ShieldAlert', tier: 'active', isActive: true },
  { id: 'surface_discovery', name: 'Attack Surface Discovery', desc: 'Descubre GitHub repos, cloud buckets, email leaks, WHOIS y servicios expuestos', icon: 'Globe', tier: 'active', isActive: true },
  { id: 'darkweb_intel', name: 'Dark Web Intelligence', desc: 'Busca credenciales expuestas, filtraciones de datos y menciones en dark web', icon: 'Eye', tier: 'active', isActive: true },
  { id: 'api_security', name: 'API Security (OWASP Top 10)', desc: 'Testing de APIs REST contra OWASP API Security Top 10: BOLA, auth bypass, SSRF', icon: 'Code', tier: 'active', isActive: true },
  { id: 'threat_intel', name: 'Threat Intelligence', desc: 'Correlacion con CISA KEV, EPSS scores, y CVEs recientes del NVD', icon: 'AlertTriangle', tier: 'active', isActive: true },
  { id: 'compliance_map', name: 'Mapeo de Cumplimiento', desc: 'Mapea hallazgos a PCI-DSS 4.0, ISO 27001, SOC 2, NIST, Ley 1581', icon: 'ClipboardCheck', tier: 'active' },
];

type TabId = 'free' | 'pro' | 'pentest';

const TABS: { id: TabId; label: string; sublabel: string; icon: any; color: string; activeColor: string; modules: ScanModule[]; tier: 'free' | 'deep' | 'active'; count: number }[] = [
  { id: 'free', label: 'Free Scan', sublabel: '9 modulos', icon: Shield, color: 'text-emerald-600', activeColor: 'border-emerald-500 bg-emerald-50 text-emerald-700', modules: FREE_MODULES, tier: 'free', count: FREE_MODULES.length - 1 },
  { id: 'pro', label: 'Pro Scan', sublabel: '5 herramientas', icon: Rocket, color: 'text-orange-500', activeColor: 'border-orange-500 bg-orange-50 text-orange-700', modules: DEEP_MODULES, tier: 'deep', count: DEEP_MODULES.length - 1 },
  { id: 'pentest', label: 'Automated Pentest', sublabel: '20 modulos activos', icon: Zap, color: 'text-red-500', activeColor: 'border-red-500 bg-red-50 text-red-700', modules: ACTIVE_MODULES, tier: 'active', count: ACTIVE_MODULES.length - 1 },
];

export default function NewScanPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { plan, isFree, scansRemaining, dailyLimit, canScanToday } = usePlan();

  const [activeTab, setActiveTab] = useState<TabId>('free');
  const [target, setTarget] = useState(searchParams.get('target') || '');
  const [selectedTypes, setSelectedTypes] = useState<string[]>(['quick']);
  const [nombre, setNombre] = useState('');
  const [consentActive, setConsentActive] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // Contexto de caja gris/blanca. Vacio = caja negra (comportamiento por defecto).
  const [scanContext, setScanContext] = useState<ScanContextItem[]>([]);

  const cleanDomain = (input: string): string => {
    let d = input.trim().toLowerCase();
    d = d.replace(/^https?:\/\//, '');
    d = d.split('/')[0];
    d = d.split(':')[0];
    d = d.replace(/^www\./, '');
    return d;
  };

  const cleanedTarget = cleanDomain(target);
  const isDomainDifferent = target.trim() !== '' && cleanedTarget !== target.trim().toLowerCase();
  const hasActiveSelected = selectedTypes.some(t => ACTIVE_MODULES.some(m => m.id === t));
  const currentTab = TABS.find(t => t.id === activeTab)!;
  const isTabLocked = isFree && activeTab !== 'free';

  const handleTabClick = (tabId: TabId) => {
    setActiveTab(tabId);
    // Auto-select the composite type for the tab
    if (tabId === 'free') setSelectedTypes(['quick']);
    else if (tabId === 'pro') {
      if (isFree) { setShowUpgrade(true); return; }
      setSelectedTypes(['deep']);
    }
    else if (tabId === 'pentest') {
      if (isFree) { setShowUpgrade(true); return; }
      setSelectedTypes(['pentest']);
    }
  };

  const toggleType = (id: string, tier: string) => {
    if (isFree && (tier === 'deep' || tier === 'active')) {
      setShowUpgrade(true);
      return;
    }
    if (['quick', 'deep', 'pentest'].includes(id)) {
      setSelectedTypes([id]);
      return;
    }
    setSelectedTypes(prev => {
      const without = prev.filter(t => !['quick', 'deep', 'pentest'].includes(t));
      if (without.includes(id)) {
        const result = without.filter(t => t !== id);
        return result.length === 0 ? ['quick'] : result;
      }
      return [...without, id];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!canScanToday) {
      setError(`Limite diario alcanzado (${dailyLimit} escaneos). Actualiza tu plan para mas.`);
      return;
    }
    setIsLoading(true);
    const finalTarget = cleanedTarget;
    if (!finalTarget || finalTarget.length < 3) {
      setError('Ingrese un dominio valido (ej: ejemplo.com)');
      setIsLoading(false);
      return;
    }
    try {
      const response = await scansApi.create({
        target: finalTarget,
        scan_types: selectedTypes,
        nombre: nombre || undefined,
        consent_active: hasActiveSelected ? consentActive : undefined,
        context: scanContext.length > 0 ? scanContext : undefined,
      });
      if (response.success) navigate(`/app/scans/${response.data.scan_id}`);
    } catch (err: any) {
      setError(err.message || 'Error al crear el escaneo');
    } finally {
      setIsLoading(false);
    }
  };

  const renderModuleGrid = (modules: ScanModule[], sectionTier: 'free' | 'deep' | 'active') => {
    const isLocked = isFree && sectionTier !== 'free';
    return (
      <div className={`grid gap-3 sm:grid-cols-2 ${isLocked ? 'opacity-60' : ''}`}>
        {modules.map((mod) => {
          const isSelected = selectedTypes.includes(mod.id);
          const isComposite = ['quick', 'deep', 'pentest'].includes(mod.id);
          const IconComponent = ICON_MAP[mod.icon] || Shield;
          return (
            <div key={mod.id} className={`relative ${isComposite ? 'sm:col-span-2' : ''}`}>
              <button
                type="button"
                onClick={() => toggleType(mod.id, mod.tier)}
                className={`w-full flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all duration-200 ${
                  isSelected && mod.tier === 'active'
                    ? 'border-red-400 bg-gradient-to-br from-red-50 to-orange-50 shadow-md ring-2 ring-red-300'
                    : isSelected && mod.tier === 'deep'
                    ? 'border-orange-400 bg-gradient-to-br from-orange-50 to-amber-50 shadow-md ring-2 ring-orange-300'
                    : isSelected
                    ? 'border-purple-400 bg-purple-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`mt-0.5 rounded-lg p-2 ${
                  isSelected ? (mod.tier === 'active' ? 'bg-red-100' : mod.tier === 'deep' ? 'bg-orange-100' : 'bg-purple-100') : 'bg-gray-100'
                }`}>
                  <IconComponent className={`h-4 w-4 ${
                    isSelected ? (mod.tier === 'active' ? 'text-red-600' : mod.tier === 'deep' ? 'text-orange-600' : 'text-purple-600') : 'text-gray-400'
                  }`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-sm font-medium ${isSelected ? 'text-gray-900' : 'text-gray-700'}`}>{mod.name}</span>
                    {isComposite && mod.tier === 'free' && <span className="badge bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 text-[10px]">Seleccionar todos</span>}
                    {isComposite && mod.tier === 'deep' && <span className="badge bg-orange-100 text-orange-700 ring-1 ring-orange-200 text-[10px]">Seleccionar todos</span>}
                    {isComposite && mod.tier === 'active' && <span className="badge bg-red-100 text-red-700 ring-1 ring-red-200 text-[10px]">Seleccionar todos</span>}
                  </div>
                  <p className="mt-0.5 text-xs text-gray-500 truncate">{mod.desc}</p>
                </div>
                <div className={`mt-1 flex h-5 w-5 items-center justify-center rounded-full border transition-colors flex-shrink-0 ${
                  isSelected ? 'border-purple-600 bg-purple-600' : 'border-gray-300'
                }`}>
                  {isSelected && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                </div>
              </button>
              {isLocked && <ModuleLock tier={sectionTier as 'deep' | 'active'} />}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nuevo Escaneo</h1>
          <p className="mt-0.5 text-sm text-gray-500">Selecciona el tipo de analisis y el objetivo</p>
        </div>
        <div className="flex items-center gap-3">
          <PlanBadge plan={plan} />
          <span className="text-sm text-gray-500">{scansRemaining}/{dailyLimit} hoy</span>
        </div>
      </div>

      {!canScanToday && <UpgradePrompt variant="inline" feature="Escaneos adicionales" />}

      {error && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="h-4 w-4 flex-shrink-0" />{error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Target input */}
        <div className="card">
          <div className="flex gap-4">
            <div className="flex-1">
              <label className="label">Objetivo *</label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text" value={target} onChange={(e) => setTarget(e.target.value)}
                  className="input pl-10 py-3" placeholder="ejemplo.com o 203.0.113.1" required
                />
              </div>
              {isDomainDifferent && cleanedTarget && (
                <p className="mt-1.5 text-xs text-purple-600 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" /> Se escaneara: <strong>{cleanedTarget}</strong>
                </p>
              )}
            </div>
            <div className="w-64">
              <label className="label">Nombre (opcional)</label>
              <input type="text" value={nombre} onChange={(e) => setNombre(e.target.value)} className="input py-3" placeholder="Escaneo mensual" />
            </div>
          </div>
        </div>

        {/* ═══ TABS ═══ */}
        <div className="card !p-0 overflow-hidden">
          {/* Tab bar */}
          <div className="flex border-b border-gray-200">
            {TABS.map((tab) => {
              const isActive = activeTab === tab.id;
              const TabIcon = tab.icon;
              const locked = isFree && tab.id !== 'free';
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => handleTabClick(tab.id)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3.5 text-sm font-medium transition-all border-b-2 ${
                    isActive
                      ? tab.activeColor + ' border-b-2'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <TabIcon className={`h-4 w-4 ${isActive ? '' : tab.color}`} />
                  <span className="hidden sm:inline">{tab.label}</span>
                  <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    isActive
                      ? tab.id === 'free' ? 'bg-emerald-200 text-emerald-800'
                        : tab.id === 'pro' ? 'bg-orange-200 text-orange-800'
                        : 'bg-red-200 text-red-800'
                      : 'bg-gray-100 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                  {locked && <Lock className="h-3 w-3 text-amber-500" />}
                </button>
              );
            })}
          </div>

          {/* Tab content */}
          <div className="p-5">
            {/* Tab description */}
            <p className="text-sm text-gray-500 mb-4">
              {activeTab === 'free' && 'Reconocimiento pasivo: headers, SSL, DNS, tecnologias, puertos, archivos, subdominios, paneles admin y CVEs.'}
              {activeTab === 'pro' && 'Herramientas avanzadas de escaneo: Nmap (1000 puertos), Nuclei (CVEs), Nikto, Subfinder, testssl.sh.'}
              {activeTab === 'pentest' && 'Pentesting automatizado: fuerza bruta, CSRF, clickjacking, SQL injection, XSS, enumeracion y mas.'}
            </p>

            {/* Module grid for active tab */}
            {renderModuleGrid(currentTab.modules, currentTab.tier)}

            {/* Consent for active modules */}
            {activeTab === 'pentest' && hasActiveSelected && !isFree && (
              <div className="mt-4 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4">
                <input
                  type="checkbox" id="consent" checked={consentActive}
                  onChange={(e) => setConsentActive(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-red-300 text-red-600 focus:ring-red-500"
                />
                <label htmlFor="consent" className="text-sm text-red-800">
                  <strong>Confirmo que tengo autorizacion explicita</strong> para realizar pruebas activas en este objetivo.
                  Estos modulos envian payloads de prueba al servidor.
                </label>
              </div>
            )}

            {/* Contexto de caja gris/blanca — solo planes pagos */}
            {!isFree && (
              <div className="mt-4">
                <ScanContextForm
                  value={scanContext}
                  onChange={setScanContext}
                  disabled={isLoading}
                />
              </div>
            )}

            {/* Upgrade prompt inside locked tabs */}
            {isTabLocked && (
              <div className="mt-4">
                <UpgradePrompt
                  variant="inline"
                  feature={activeTab === 'pro' ? 'Escaneo Profundo' : 'Pentesting Automatizado'}
                />
              </div>
            )}
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            {selectedTypes.length} modulo(s) seleccionado(s)
            {hasActiveSelected && ' — incluye pentesting activo'}
          </p>
          <button
            type="submit"
            disabled={isLoading || !target || !canScanToday || (hasActiveSelected && !consentActive)}
            className="btn-primary"
          >
            {isLoading ? (
              <><div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" /> Iniciando...</>
            ) : (
              <><Zap className="h-5 w-5" /> Iniciar Escaneo</>
            )}
          </button>
        </div>
      </form>

      {showUpgrade && <UpgradePrompt variant="modal" onClose={() => setShowUpgrade(false)} />}
    </div>
  );
}
